import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, Eye } from "lucide-react";
import { getNotifications, getNotificationDetail } from "@/lib/portalApi";
import { formatDateTime } from "@/components/portal/portalConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLES = {
  sent: "bg-venka-green/15 text-venka-green-light border-venka-green/30",
  demo: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
};

const LANG_LABEL = { en: "EN", pt: "PT", fr: "FR" };

export const PortalNotifications = () => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    getNotifications()
      .then(setRows)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openPreview = async (id) => {
    try {
      const d = await getNotificationDetail(id);
      setPreview(d);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-notifications">
      <div>
        <h1 className="text-2xl font-bold">{t("portal.notifications.title")}</h1>
        <p className="text-sm text-slate-400">{t("portal.notifications.subtitle")}</p>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-xs text-amber-200">
        {t("portal.notifications.demoNote")}
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/70 text-slate-400">
              <tr>
                <th className="text-left font-medium px-4 py-3">{t("portal.notifications.event")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.notifications.subject")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.notifications.recipient")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.notifications.language")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.notifications.status")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.notifications.when")}</th>
                <th className="text-right font-medium px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="notifications-empty">{t("portal.notifications.empty")}</td></tr>
              )}
              {!loading && rows.map((n) => (
                <tr key={n.id} className="hover:bg-slate-800/40" data-testid={`notification-row-${n.id}`}>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-500" />{t(`portal.notifications.events.${n.event_type}`)}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 max-w-[280px] truncate">{n.subject}</td>
                  <td className="px-4 py-3 text-slate-400">{n.email}</td>
                  <td className="px-4 py-3"><span className="text-[11px] font-bold text-venka-green-light">{LANG_LABEL[n.language] || n.language}</span></td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[n.status] || STATUS_STYLES.demo}`}>
                      {t(`portal.notifications.statuses.${n.status}`, n.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{formatDateTime(n.created_at, i18n.resolvedLanguage)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openPreview(n.id)} className="text-venka-green-light inline-flex items-center gap-1 text-xs" data-testid={`notification-view-${n.id}`}>
                      <Eye className="h-3.5 w-3.5" /> {t("portal.notifications.view")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("portal.notifications.preview")}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <p className="text-sm"><span className="text-slate-400">{t("portal.notifications.subject")}:</span> {preview.subject}</p>
              <div className="rounded-lg bg-white p-2 max-h-[60vh] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: preview.body_html }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalNotifications;
